/**
 * Las redes sociales que la automotora cargó en el admin
 * (Configuración → General → Redes sociales), listas para pintar.
 *
 * Viven en `clients.seo.social_links` y hasta ahora su único consumidor era el
 * `sameAs` del JSON-LD. El footer del builder las usa para armar una columna de
 * "Redes sociales" sin que nadie tenga que volver a tipear los links.
 *
 * El orden es fijo (Instagram → Facebook → TikTok → YouTube) para que el footer
 * no cambie de orden entre tenants ni entre renders.
 */

export type RedSocial = {
  platform: 'instagram' | 'facebook' | 'tiktok' | 'youtube';
  /** Nombre visible de la red. */
  label: string;
  url: string;
};

const ORDEN: { platform: RedSocial['platform']; label: string }[] = [
  { platform: 'instagram', label: 'Instagram' },
  { platform: 'facebook', label: 'Facebook' },
  { platform: 'tiktok', label: 'TikTok' },
  { platform: 'youtube', label: 'YouTube' },
];

/**
 * Deja el link listo para un href, o '' si no se puede confiar en él.
 *
 * En producción hay valores cargados a mano que no son URLs: TikTok sin esquema
 * ("www.tiktok.com/@x", que sin arreglar sale como link relativo dentro del
 * propio sitio) y un "htpps://" con el typo. A ese último NO se le puede
 * anteponer https:// — quedaría "https://htpps://…", un link muerto — así que
 * se descarta. Cualquier esquema que no sea http/https se descarta también:
 * javascript: y data: en un href son un agujero de XSS.
 */
export const normalizarUrlSocial = (platform: string, crudo: unknown): string => {
  // `unknown` a propósito: esto sale de un jsonb, no de un tipo declarado. Con
  // `(crudo || '').trim()` un número o un objeto tiraban TypeError, y el error
  // boundary del builder envuelve el <Frame> ENTERO: un throw acá dejaba la
  // página del tenant en blanco, no solo el footer.
  const v = typeof crudo === 'string' ? crudo.trim() : '';
  if (!v || v === '#') return '';

  // En TikTok lo natural es copiar el usuario, no la URL.
  if (platform === 'tiktok' && v.startsWith('@')) {
    return `https://www.tiktok.com/${v}`;
  }

  // ¿Trae algo con pinta de esquema? Entonces tiene que ser http(s) de verdad.
  const conEsquema = /^([a-z][a-z0-9+.-]*):/i.exec(v);
  if (conEsquema) {
    return /^https?$/i.test(conEsquema[1]) ? v : '';
  }

  // Sin esquema el href sale relativo y el link cae dentro del propio sitio.
  return `https://${v}`;
};

type ClienteConRedes = {
  seo?: { social_links?: Record<string, string> | string[] | null } | null;
} | null | undefined;

/**
 * Las redes configuradas por la automotora, normalizadas y sin las vacías.
 * Devuelve [] cuando no hay ninguna (la gran mayoría de los tenants).
 */
export const redesConfiguradas = (client: ClienteConRedes): RedSocial[] => {
  const crudo = client?.seo?.social_links;
  if (!crudo || Array.isArray(crudo)) return [];

  return ORDEN.map(({ platform, label }) => ({
    platform,
    label,
    url: normalizarUrlSocial(platform, crudo[platform]),
  })).filter((r) => r.url);
};
